class CashleticzInfiniteScroll {
  constructor(container) {
    this.container = container;
    this.grid = container.querySelector('#product-grid');
    this.paginationWrapper = container.querySelector('.pagination-wrapper');
    this.loading = false;
    this.loadedUrls = new Set();

    if (!this.grid || !this.paginationWrapper) return;

    this.sentinel = document.createElement('div');
    this.sentinel.className = 'cashleticz-infinite-scroll';
    this.sentinel.setAttribute('aria-live', 'polite');
    this.sentinel.innerHTML = `
      <span class="cashleticz-infinite-scroll__spinner" aria-hidden="true"></span>
      <span class="cashleticz-infinite-scroll__text">Loading more products</span>
    `;
    this.paginationWrapper.insertAdjacentElement('afterend', this.sentinel);

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: '800px 0px' }
    );

    this.observer.observe(this.sentinel);
    this.updateState();
  }

  getNextUrl() {
    const nextLink = this.paginationWrapper?.querySelector('.pagination__item--prev[href]');
    return nextLink ? new URL(nextLink.href, window.location.origin).toString() : null;
  }

  async loadNextPage() {
    const nextUrl = this.getNextUrl();
    if (!nextUrl || this.loading || this.loadedUrls.has(nextUrl)) {
      if (!nextUrl) this.finish();
      return;
    }

    this.loading = true;
    this.loadedUrls.add(nextUrl);
    this.sentinel.classList.add('is-loading');

    try {
      const response = await fetch(nextUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Infinite scroll request failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('#ProductGridContainer #product-grid');
      const newPagination = doc.querySelector('#ProductGridContainer .pagination-wrapper');

      if (!newGrid) throw new Error('The next product grid could not be found.');

      const items = Array.from(newGrid.children);
      const fragment = document.createDocumentFragment();
      items.forEach((item) => {
        item.classList.add('cashleticz-product-reveal');
        fragment.appendChild(item);
      });
      this.grid.appendChild(fragment);

      if (newPagination) {
        this.paginationWrapper.innerHTML = newPagination.innerHTML;
      } else {
        this.paginationWrapper.remove();
        this.paginationWrapper = null;
      }

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(this.grid);
      }

      document.dispatchEvent(new CustomEvent('cashleticz:products-loaded', {
        detail: { count: items.length, url: nextUrl }
      }));
    } catch (error) {
      console.error(error);
      this.loadedUrls.delete(nextUrl);
      this.sentinel.classList.add('has-error');
      this.sentinel.querySelector('.cashleticz-infinite-scroll__text').textContent =
        'Unable to load more products. Scroll again to retry.';
    } finally {
      this.loading = false;
      this.sentinel.classList.remove('is-loading');
      this.updateState();
    }
  }

  updateState() {
    if (!this.sentinel) return;
    const hasNextPage = Boolean(this.getNextUrl());
    this.sentinel.hidden = !hasNextPage;
    if (!hasNextPage) this.finish();
  }

  finish() {
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.hidden = true;
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.remove();
  }
}

let cashleticzInfiniteScrollInstance;

function initializeCashleticzInfiniteScroll() {
  const container = document.querySelector('#ProductGridContainer');
  if (!container) return;

  if (cashleticzInfiniteScrollInstance?.container === container) return;
  cashleticzInfiniteScrollInstance?.destroy();
  cashleticzInfiniteScrollInstance = new CashleticzInfiniteScroll(container);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCashleticzInfiniteScroll);
} else {
  initializeCashleticzInfiniteScroll();
}

// Dawn replaces the product-grid container after filtering and sorting.
const cashleticzGridObserver = new MutationObserver(() => initializeCashleticzInfiniteScroll());
cashleticzGridObserver.observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', initializeCashleticzInfiniteScroll);
